import {
  type CanadaProvinceCode,
  type UsaStateCode,
} from '@webapp/core/entities/enums/StateProvince'
import { PaymentMethodTypes } from '../../entities/enums/PaymentMethodTypeEnum'
import { type PaymentMethodConstructor } from '../../entities/PaymentMethod'
import { type PaymentMethodApiModel } from '../api-models/PaymentMethodApiModel'

export const mapPaymentMethodApiModel = (
  apiModel: PaymentMethodApiModel
): PaymentMethodConstructor => {
  const baseData = {
    id: apiModel.id,
    name: apiModel.name,
    currency: {
      id: apiModel.currency.id,
      name: apiModel.currency.name,
      rate: Number.parseFloat(apiModel.currency.rate),
      base: apiModel.currency.base,
    },
    vendorId: apiModel.vendor,
    paymentRail: apiModel.payment_rail,
    bankAccountCountryCode: apiModel.bank_account_country_code,
    flightTime: apiModel.flight_time || null,
    transactionFee: apiModel.transaction_fee,
  }

  switch (apiModel.type) {
    case PaymentMethodTypes.OTHER:
      return {
        ...baseData,
        type: PaymentMethodTypes.OTHER,
        data: {
          description: apiModel.data.description,
        },
      }

    case PaymentMethodTypes.CHECK:
      return {
        ...baseData,
        type: PaymentMethodTypes.CHECK,
        data: {
          payableTo: apiModel.data.payable_to ?? '',
        },
      }

    case PaymentMethodTypes.ACH:
      return {
        ...baseData,
        type: PaymentMethodTypes.ACH,
        data: {
          routingNumber: apiModel.data.routing_number ?? '',
          companyName: apiModel.data.company_name ?? '',
          accountNumber: apiModel.data.account_number ?? '',
        },
      }

    case PaymentMethodTypes.EFT:
      return {
        ...baseData,
        type: PaymentMethodTypes.EFT,
        data: {
          bankAccountHolderName: apiModel.data.bank_account_holder_name ?? '',
          bankName: apiModel.data.bank_name ?? null,
          institutionNumber: apiModel.data.institution_number ?? '',
          transitNumber: apiModel.data.transit_number ?? '',
          accountNumber: apiModel.data.account_number ?? '',
          payeeName: apiModel.data.payee_name ?? '',
          streetAddress: apiModel.data.street_address ?? '',
          city: apiModel.data.city ?? '',
          province: (apiModel.data.province ?? '') as CanadaProvinceCode,
          postalCode: apiModel.data.postal_code ?? '',
        },
      }

    case PaymentMethodTypes.PROCURIFY_ACH:
    case PaymentMethodTypes.PROCURIFY_WIRE:
      return {
        ...baseData,
        type: apiModel.type,
        data: {
          routingNumber: apiModel.data.routing_number ?? '',
          payeeName: apiModel.data.payee_name ?? '',
          accountNumber: apiModel.data.account_number ?? '',
          bankAccountHolderName: apiModel.data.bank_account_holder_name ?? '',
          bankName: apiModel.data.bank_name ?? null,
          streetAddress: apiModel.data.street_address ?? '',
          city: apiModel.data.city ?? '',
          state: (apiModel.data.state ?? '') as UsaStateCode,
          zipCode: apiModel.data.zip_code ?? '',
        },
      }

    case PaymentMethodTypes.PROCURIFY_CHECK:
      return {
        ...baseData,
        type: PaymentMethodTypes.PROCURIFY_CHECK,
        data: {
          payeeName: apiModel.data.payee_name ?? '',
          streetAddress: apiModel.data.street_address ?? '',
          city: apiModel.data.city ?? '',
          state: (apiModel.data.state ?? '') as UsaStateCode,
          zipCode: apiModel.data.zip_code ?? '',
        },
      }

    case PaymentMethodTypes.AIRWALLEX: {
      const beneficiary = apiModel.data.beneficiary
      const bankDetails = beneficiary?.bank_details
      const address = beneficiary?.address

      return {
        ...baseData,
        type: PaymentMethodTypes.AIRWALLEX,
        data:
          beneficiary && bankDetails
            ? {
                accountCurrency: bankDetails.account_currency,
                accountName: bankDetails.account_name,
                accountNumber: bankDetails.account_number,
                accountNameAlias: bankDetails.account_name_alias,
                accountRoutingType1: bankDetails.account_routing_type1,
                accountRoutingValue1: bankDetails.account_routing_value1,
                accountRoutingType2: bankDetails.account_routing_type2,
                accountRoutingValue2: bankDetails.account_routing_value2,
                bankAccountCategory: bankDetails.bank_account_category,
                bankBranch: bankDetails.bank_branch,
                bankCountryCode: bankDetails.bank_country_code,
                bankState: bankDetails.bank_state,
                bindingMobileNumber: bankDetails.binding_mobile_number,
                companyName: beneficiary.company_name,
                localClearingSystem: bankDetails.local_clearing_system,
                entityType: beneficiary.entity_type,
                fingerprint: bankDetails.fingerprint,
                iban: bankDetails.iban,
                intermediaryBankName: bankDetails.intermediary_bank_name,
                intermediaryBankSwiftCode:
                  bankDetails.intermediary_bank_swift_code,
                swiftCode: bankDetails.swift_code,
                address: address
                  ? {
                      city: address.city,
                      countryCode: address.country_code,
                      postcode: address.postcode,
                      state: address.state,
                      streetAddress: address.street_address,
                    }
                  : {},
              }
            : {
                accountCurrency: apiModel.currency.name,
                accountName: apiModel.name,
                bankCountryCode: apiModel.bank_account_country_code ?? '',
                address: {},
              },
      }
    }

    default:
      return {
        ...baseData,
        type: PaymentMethodTypes.OTHER,
        data: {},
      }
  }
}
